class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector("ul");
    this.sliderItems = this.querySelectorAll("li");
    this.pageCount = this.querySelector(".slider-counter--current");
    this.pageTotal = this.querySelector(".slider-counter--total");
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');
    this.dotButton = this.querySelectorAll('button[name="dots"]');
    this.rtl_num = document.body.classList.contains("layout_rtl") ? -1 : 1;

    this._onScroll = this.update.bind(this);
    this._onBtn = this.onButtonClick.bind(this);
    this._onDot = this.onDotClick.bind(this);

    this._eventsAttached = false;
    this._resizeScheduled = false;

    if (!this.slider || !this.nextButton) return;

    this._setupResizeObserver();
    this._attachEvents();

    requestAnimationFrame(() => this.initPages());
  }

  _setupResizeObserver() {
    this._ro?.disconnect();
    this._ro = new ResizeObserver((entries) => {
      window.requestAnimationFrame(() => {
        if (!Array.isArray(entries) || !entries.length) return;
        
        const entry = entries[0];
        if (!entry) return;

        if (this._resizeScheduled) return;
        this._resizeScheduled = true;

        setTimeout(() => {
          this._resizeScheduled = false;
          this.initPages();
        }, 20);
      });
    });
    this._ro.observe(this.slider);
  }
  
  _attachEvents() {
    if (this._eventsAttached) return;
    this.slider.addEventListener("scroll", this._onScroll, { passive: true });
    this.prevButton?.addEventListener("click", this._onBtn);
    this.nextButton?.addEventListener("click", this._onBtn);
    this.dotButton.forEach((b) => b.addEventListener("click", this._onDot));
    this._eventsAttached = true;
  }

  _detachEvents() {
    if (!this._eventsAttached) return;
    this.slider?.removeEventListener("scroll", this._onScroll);
    this.prevButton?.removeEventListener("click", this._onBtn);
    this.nextButton?.removeEventListener("click", this._onBtn);
    this.dotButton.forEach((b) => b.removeEventListener("click", this._onDot));
    this._eventsAttached = false;
  }

  disconnectedCallback() {
    this._ro?.disconnect();
    this._detachEvents();
  }

  initPages() {
    if (!this.sliderItems?.length) return;
    const itemW = this.sliderItems[0].clientWidth || 1;
    const sliderW = this.slider.clientWidth || 1;

    const slidesPerPage = Math.floor(sliderW / itemW);
    const totalPages = this.sliderItems.length - slidesPerPage + 1;

    if (this.slidesPerPage !== slidesPerPage) {
      this.slidesPerPage = slidesPerPage;
      if (this.pageCount) {
        const nextVal = String(this.slidesPerPage);
        if (this.pageCount.innerText !== nextVal) this.pageCount.innerText = nextVal;
      }
    }
    this.totalPages = totalPages;
  }

  update() {
    if (!this.sliderItems?.length) return;
    const itemW = this.sliderItems[0].clientWidth || 1;
    const nextPage = Math.round((this.slider.scrollLeft / itemW) * this.rtl_num);

    if (this.currentPage !== nextPage) {
      this.currentPage = nextPage;

      const active = this.querySelector(".dots-item.active");
      if (active) active.classList.remove("active");
      const dots = this.querySelectorAll(".dots-item");
      if (dots[this.currentPage]) dots[this.currentPage].classList.add("active");

      if (this.pageCount) {
        const val = String(this.slidesPerPage + this.currentPage);
        if (this.pageCount.innerText !== val) this.pageCount.innerText = val;
      }
    }
  }

  onButtonClick(event) {
    event.preventDefault();
    if (!this.sliderItems?.length) return;

    const step = this.sliderItems[0].clientWidth * this.rtl_num || 0;
    const dir = event.currentTarget.name === "next" ? 1 : -1;
    const slideScrollPosition = this.slider.scrollLeft + dir * step;

    this.slider.scrollTo({ left: slideScrollPosition, behavior: "smooth" });
  }

  onDotClick(event) {
    event.preventDefault();
    const dotActive = this.querySelector(".dots-item.active button");
    const sliderItem = Number(event.currentTarget.dataset.index || 0);
    const slideScrollPosition = sliderItem * (this.sliderItems[0]?.clientWidth || 0);

    if (!dotActive || Number(dotActive.dataset.index) !== sliderItem) {
      this.querySelector(".dots-item.active")?.classList.remove("active");
      event.currentTarget.closest(".dots-item")?.classList.add("active");
    }

    this.slider.scrollTo({ left: slideScrollPosition, behavior: "smooth" });
  }
}

customElements.define("slider-component", SliderComponent);

class ProductSliderComponent extends SliderComponent {
  constructor() {
    super();
    this._retarget();

    if (!this.slider || !this.nextButton) return;

    this._setupResizeObserver();
    this._detachEvents();
    this._attachEvents();

    this._mo?.disconnect();
    this._mo = new MutationObserver(() => {
      const prevSlider = this.slider;
      this._retarget();
      if (this.slider && this.slider !== prevSlider) {
        this._setupResizeObserver();
        this._detachEvents();
        this._attachEvents();
      }
      requestAnimationFrame(() => this.initPages());
    });

    this._mo.observe(this.slider, { subtree: true, childList: true });
  }

  _retarget() {
    this.slider = this.querySelector(".slider");
    const firstSet = this.querySelectorAll(".slider__slide");
    this.sliderItems = firstSet.length ? firstSet : this.querySelectorAll(".slider div.product");
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._mo?.disconnect();
  }
}

class BannerSliderComponent extends SliderComponent {
  constructor() {
    super();
    this.slider = this.querySelector(".slider");
    this.sliderItems = this.querySelectorAll(".slider__slide");

    if (!this.slider || !this.nextButton) return;

    this._setupResizeObserver();
    this._detachEvents();
    this._attachEvents();

    this._mo?.disconnect();
    this._mo = new MutationObserver(() => {
      const prevSlider = this.slider;
      this.slider = this.querySelector(".slider");
      this.sliderItems = this.querySelectorAll(".slider__slide");
      if (this.slider && this.slider !== prevSlider) {
        this._setupResizeObserver();
        this._detachEvents();
        this._attachEvents();
      }
      requestAnimationFrame(() => this.initPages());
    });

    this._mo.observe(this.slider, { subtree: true, childList: true });

    this.initButtonDisable();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._mo?.disconnect();
    this._io?.disconnect();
  }

  initButtonDisable() {
    this._io?.disconnect();

    if (!this.sliderItems?.length) return;

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const isFirst = entry.target.classList.contains("first-item");
          const isLast = entry.target.classList.contains("last-item");
          if (entry.isIntersecting) {
            if (isFirst) this.prevButton?.classList.add("disabled");
            if (isLast) this.nextButton?.classList.add("disabled");
          } else {
            if (isFirst) this.prevButton?.classList.remove("disabled");
            if (isLast) this.nextButton?.classList.remove("disabled");
          }
        });
      },
      { threshold: 0.8, root: this.slider }
    );

    this.sliderItems[0].classList.add("first-item");
    this.sliderItems[this.sliderItems.length - 1].classList.add("last-item");

    io.observe(this.sliderItems[0]);
    io.observe(this.sliderItems[this.sliderItems.length - 1]);

    this._io = io;
  }
}

class TiktokSliderComponent extends BannerSliderComponent {
  constructor() {
    super();
    this.sliderActionsAndPageCount = this.querySelector(".slider-buttons");
  }

  initPages() {
    if (!this.sliderItems?.length) return;

    const distance = (this.slider.clientWidth || 1) / (this.sliderItems[0].clientWidth || 1);
    const slidesPerPage = distance % 1 > 0.5 ? Math.ceil(distance) : Math.floor(distance);
    const totalPages = Math.ceil(this.sliderItems.length - slidesPerPage) + 1;

    if (this.pageTotal && this.dataset.tiktokSlider === "true") {
      const nextTotal = String(totalPages);
      if (this.pageTotal.innerText !== nextTotal) this.pageTotal.innerText = nextTotal;
    }

    this.slidesPerPage = slidesPerPage;
    this.totalPages = totalPages;

    if (this.sliderActionsAndPageCount) {
      const shouldShow = this.totalPages > 1 && window.innerWidth < 1025;
      const desired = shouldShow ? "flex" : "none";
      if (this.sliderActionsAndPageCount.style.display !== desired) {
        this.sliderActionsAndPageCount.style.display = desired;
      }
    }
  }
}

window.addEventListener("load", () => {
  customElements.define("banner-slider-component", BannerSliderComponent);
  customElements.define("product-slider-component", ProductSliderComponent);
  customElements.define("tiktok-slider-component", TiktokSliderComponent);
});

function syncSliderWithVariant() {
  function waitForSlickInit(selector, callback, maxAttempts = 30) {
    let attempts = 0;

    function checkSlick() {
      const element = document.querySelector(selector);
      if (!element) {
        attempts++;
        if (attempts < maxAttempts) setTimeout(checkSlick, 200);
        return;
      }
      if ($(element).hasClass("slick-initialized")) {
        callback(element);
      } else {
        attempts++;
        if (attempts < maxAttempts) setTimeout(checkSlick, 200);
      }
    }
    checkSlick();
  }

  function resolveIndexByColor(sliderEl, color) {
    if (!sliderEl || !color) return null;

    const direct = sliderEl.querySelector(`.slick-slide[data-media-color="${color}"]:not(.slick-cloned)`);
    let idx = direct?.getAttribute("data-slick-index");

    if (idx == null) {
      const inner = sliderEl.querySelector(`[data-media-color="${color}"]`);
      if (inner) {
        const slide = inner.closest(".slick-slide");
        if (slide && !slide.classList.contains("slick-cloned")) {
          idx = slide.getAttribute("data-slick-index");
        }
      }
    }
    return idx != null ? parseInt(idx, 10) : null;
  }

  function performSync() {
    const navContainer = document.querySelector(".productView-nav");
    if (!navContainer) return;

    const currentColor = navContainer.getAttribute("data-current-media-color");
    if (!currentColor) return;

    waitForSlickInit(".productView-for.slick-slider", function (mainSlider) {
      const mainIndex = resolveIndexByColor(mainSlider, currentColor);
      if (mainIndex != null) {
        $(mainSlider).slick("slickGoTo", mainIndex, true);
      }

      setTimeout(() => {
        waitForSlickInit(".productView-thumbnail-wrapper .slick-slider", function (thumbSlider) {
          const thumbIndex = resolveIndexByColor(thumbSlider, currentColor);
          if (thumbIndex != null) {
            $(thumbSlider).slick("slickGoTo", thumbIndex, true);
          }
        });
      }, 200);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      setTimeout(performSync, 400);
    });
  } else {
    setTimeout(performSync, 400);
  }
}

syncSliderWithVariant();

document.addEventListener("DOMContentLoaded", function () {
  const nav = document.querySelector(".productView-nav");
  if (!nav) return;

  const observer = new MutationObserver(() => {
    syncSliderWithVariant();
  });
  observer.observe(nav, { attributes: true, attributeFilter: ["data-current-media-color"] });
});